import { CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from "@mui/material"
import { LoadingButton } from "@mui/lab"
import { useEffect, useMemo, useState } from "react"
import { NavLink, useHistory } from "react-router-dom"
import AutocompleteDeluxe from "../../components/AutocompleteDelux"
import Input from "../../components/Input"
import Vista from "../../components/Vista"
import { useLocalStorage } from "../../hooks/useLocalStorage"
import { useMessages } from "../../hooks/useMessages"
import Servo from "../../services/servo"
import { productDetails } from "../../utils/styles"

const Detail = ({ match }) => {
  const styles = productDetails()()
  const history = useHistory()
  const { setMessages } = useMessages()
  const [permiso] = useLocalStorage("permiso", {})
  const idAreaRuta = Number(match?.params?.id || 0)
  const [area, setArea] = useState(null)
  const [disponibles, setDisponibles] = useState([])
  const [asignados, setAsignados] = useState([])
  const [busqueda, setBusqueda] = useState("")
  const [cargando, setCargando] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [desasignando, setDesasignando] = useState(null)
  const puedeGuardar = (permiso?.carrosAreas & 2) === 2 || (permiso?.areas & 2) === 2 || (permiso?.carros & 2) === 2

  const cargarDisponibles = async () => {
    const respuesta = await Servo.get("carros-areas/carros-disponibles")
    setDisponibles(respuesta?.cuerpo?.resultado || [])
  }

  const cargarAsignados = async (idArea) => {
    if (!idArea) {
      setAsignados([])
      return
    }
    const respuesta = await Servo.get(`carros-areas?idProyecto=${idArea}&eliminado=false&expand=carro`)
    const asignaciones = respuesta?.cuerpo?.resultado || []
    const carrosAsignados = await Promise.all(
      asignaciones.map(async (asignacion) => {
        // La relación carro puede no venir expandida según la configuración
        // del serializador; se consulta el carro por su id como respaldo.
        if (asignacion.carro) return asignacion.carro
        const carroRespuesta = await Servo.get(`carros?idCarro=${asignacion.idCarro}`)
        return carroRespuesta?.cuerpo?.resultado?.[0] || null
      })
    )
    setAsignados(carrosAsignados.filter(Boolean))
  }

  const cargarDatos = async (idArea) => {
    setCargando(true)
    await Promise.all([cargarDisponibles(), cargarAsignados(idArea)])
    setCargando(false)
  }

  useEffect(() => { cargarDatos(idAreaRuta || null) }, [])

  useEffect(() => {
    if (!idAreaRuta) return
    const cargarArea = async () => {
      const respuesta = await Servo.get(`areas?idArea=${idAreaRuta}`)
      setArea(respuesta?.cuerpo?.resultado?.[0] || null)
    }
    cargarArea()
  }, [idAreaRuta])

  const filtrarCarros = (carros) => {
    const termino = busqueda.trim().toLowerCase()
    if (!termino) return carros
    return carros.filter((carro) =>
      [carro.economico, carro.placas, carro.marca, carro.modelo, carro.vin]
        .filter(Boolean)
        .some((valor) => String(valor).toLowerCase().includes(termino))
    )
  }

  const disponiblesFiltrados = useMemo(() => filtrarCarros(disponibles), [busqueda, disponibles])
  const asignadosFiltrados = useMemo(() => filtrarCarros(asignados), [busqueda, asignados])

  const asignar = (carro) => {
    if (!area?.idArea) {
      setMessages({ mensaje: "Seleccione un proyecto antes de asignar carros", color: "error" })
      return
    }
    setDisponibles((actuales) => actuales.filter((actual) => actual.idCarro !== carro.idCarro))
    setAsignados((actuales) => [...actuales, carro])
  }

  const desasignar = async (carro) => {
    if (!area?.idArea) return
    setDesasignando(carro.idCarro)
    const respuesta = await Servo.post("carros-areas/deshabilitar", { idProyecto: area.idArea, idCarro: carro.idCarro }, "carrosAreas", false, history)
    if (respuesta?.cuerpo) setMessages(respuesta.cuerpo)
    if (respuesta?.cuerpo?.detalle?.estatus === 200) {
      setAsignados((actuales) => actuales.filter((actual) => actual.idCarro !== carro.idCarro))
      setDisponibles((actuales) => [...actuales, carro])
    }
    setDesasignando(null)
  }

  const guardar = async () => {
    if (!area?.idArea) {
      setMessages({ mensaje: "Seleccione un proyecto antes de asignar carros", color: "error" })
      return
    }
    setGuardando(true)
    const respuesta = await Servo.post("carros-areas/guardar", { idProyecto: area.idArea, carros: asignados.map((carro) => carro.idCarro), sincronizar: true }, "carrosAreas", false, history)
    if (respuesta?.cuerpo) setMessages(respuesta.cuerpo)
    if (respuesta?.cuerpo?.detalle?.estatus === 200) {
      await cargarDatos(area.idArea)
    }
    setGuardando(false)
  }

  const tablaCarros = (titulo, carros, accion, textoAccion, color, loadingId) => (
    <Paper elevation={2} style={{ overflow: "hidden", backgroundColor: "#fff", height: "100%" }}>
      <div style={{ padding: "12px 16px", fontWeight: 600, color: "#157CC1", borderBottom: "1px solid #e0e0e0" }}>{titulo} ({carros.length})</div>
      <TableContainer style={{ maxHeight: 360 }}>
        <Table stickyHeader size="small">
          <TableHead><TableRow>
            <TableCell style={{ fontWeight: 600 }}>No. económico</TableCell>
            <TableCell style={{ fontWeight: 600 }}>Carro</TableCell>
            <TableCell style={{ fontWeight: 600 }}>Placas</TableCell>
            <TableCell align="right" style={{ fontWeight: 600 }}>Acción</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {carros.map((carro) => (
              <TableRow hover key={carro.idCarro}>
                <TableCell><strong>{carro.economico || "Sin económico"}</strong></TableCell>
                <TableCell>{[carro.marca, carro.modelo, carro.year].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell>{carro.placas || "—"}</TableCell>
                <TableCell align="right"><Button size="small" variant="outlined" color={color} disabled={!puedeGuardar || loadingId === carro.idCarro} onClick={() => accion(carro)}>{loadingId === carro.idCarro ? "Procesando..." : textoAccion}</Button></TableCell>
              </TableRow>
            ))}
            {!carros.length && <TableRow><TableCell colSpan={4} align="center">No hay carros para mostrar.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )

  return (
    <Vista>
      <div className={styles.screenSize}>
        <div className={styles.volverCarros}><NavLink exact to="/carrosAreas">« Volver</NavLink></div>
        <div className={styles.top}><div className={styles.title}><p>{idAreaRuta ? "Editar asignación de carros" : "Asignar carros a proyecto"}</p></div></div>
        <div className={styles.formArea}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} className={styles.inputArea}><AutocompleteDeluxe labelToShow="Proyecto" labelProp="nombre" idProp="idArea" nombre="areas" dato={area} setDato={(proyecto) => { setArea(proyecto); cargarDatos(proyecto?.idArea) }} required="true" /></Grid>
            <Grid item xs={12} md={6} className={styles.inputArea}><Input typeInput="form" nombre="Buscar carro" placeholder="Económico, placas, marca o modelo" valor={busqueda} onChange={(evento) => setBusqueda(evento.target.value)} /></Grid>
            {cargando ? <Grid item xs={12}><CircularProgress /></Grid> : <>
              <Grid item xs={12} md={6}>{tablaCarros("Carros sin asignar", disponiblesFiltrados, asignar, "Asignar", "primary")}</Grid>
              <Grid item xs={12} md={6}>{tablaCarros("Carros asignados al proyecto", asignadosFiltrados, desasignar, "Desasignar", "error", desasignando)}</Grid>
            </>}
          </Grid>
        </div>
        <div className={styles.buttonArea}><LoadingButton loading={guardando} disabled={!puedeGuardar || cargando} onClick={guardar} variant="contained">GUARDAR CAMBIOS</LoadingButton></div>
      </div>
    </Vista>
  )
}

export default Detail
